import { api } from '@/lib/api';
import { build_params, download_export } from '@/lib/list-query';
import type { SortState } from '@/hooks/use-url-state';
import type {
  ApprovalCounts,
  QueueDetail,
  QueueFilters,
  QueueItem,
  QueueResponse,
  Vertical,
} from './types';

/**
 * The queue, with whatever filters and sort are set.
 *
 * Through `build_params` rather than a hand-written if-chain: a filter added to the type
 * but forgotten here would look alive on screen while sending nothing.
 */
export async function fetch_product_queue(
  filters: QueueFilters,
  sort?: SortState,
): Promise<QueueResponse> {
  const res = await api.get<QueueResponse>('/admin/products', {
    params: build_params(filters as Record<string, never>, sort),
  });
  return res.data;
}

/** The tiles. Describes the whole queue, so it deliberately ignores the page's filters. */
export async function fetch_approval_counts(): Promise<ApprovalCounts> {
  const res = await api.get<ApprovalCounts>('/admin/products/counts');
  return res.data;
}

export async function export_product_queue(
  filters: QueueFilters,
  sort?: SortState,
): Promise<void> {
  await download_export(
    '/admin/products/export',
    build_params(filters as Record<string, never>, sort),
    'product-approvals.csv',
  );
}

export async function approve_product(
  vertical: Vertical,
  id: string,
  note?: string,
): Promise<QueueItem> {
  const res = await api.post<QueueItem>(`/admin/products/${vertical}/${id}/approve`, {
    ...(note ? { note } : {}),
  });
  return res.data;
}

/** The reason is required — the vendor reads it, so "no" alone is not an answer. */
export async function reject_product(
  vertical: Vertical,
  id: string,
  reason: string,
): Promise<QueueItem> {
  const res = await api.post<QueueItem>(`/admin/products/${vertical}/${id}/reject`, { reason });
  return res.data;
}

/** One product under review, with its full gallery and per-size prices. */
export async function fetch_product_detail(vertical: Vertical, id: string): Promise<QueueDetail> {
  const res = await api.get<QueueDetail>(`/admin/products/${vertical}/${id}`);
  return res.data;
}

/**
 * Override the fee on one product or one size before approving.
 *
 * The API takes exactly one of the two ids — a fee is either about this whole product or
 * about one size of it, and sending both would leave "which won" to whoever reads it.
 */
export async function override_commission(input: {
  vertical: Vertical;
  product_id?: string;
  variant_id?: string;
  mode: 'RATE' | 'FLAT' | null;
  rate_pct?: number | null;
  amount_inr?: number | null;
}): Promise<unknown> {
  const res = await api.put('/admin/commission/override', input);
  return res.data;
}
